import * as dte from "../DtEventTypes";
import {
  BACK_ELEMENT_CLONE_OVERLAY,
  COLOR_TRACE_HIGHLIGHT_ABGR,
  ELEMENT_CLONE_OVERLAY,
  NAME_PREFIX_TRACE } from
'../facets/FacetVizEffects';

import { THREE } from "@adsk/lmv";

const EPSILON = 1e-6;
const ANIM_FPS = 30;
const ANIM_FRAME_INTERVAL = 1000 / ANIM_FPS;
const ANIM_STEPS = 100;
const CONNECTION_LINE_SPEED = 10 * 0.001;
const CONNECTION_LINE_SEGMENT_LENGTH = 5;

const CANVAS_LINE_COLOR = "rgba(127, 255, 0)";
const CANVAS_LINE_SHADOW_COLOR = "rgba(0,0,0,1.0)";
const CANVAS_LINE_STROKE_WIDTH = 2;

const CANVAS_LINE_COLOR_TRACE = "rgba(5, 91, 161)";
const CANVAS_LINE_SHADOW_COLOR_TRACE = "rgba(127, 255, 0)";
const CANVAS_LINE_STROKE_WIDTH_TRACE_SECONDARY = 2;
const CANVAS_LINE_STROKE_WIDTH_TRACE = 4;


export const TRACE_ELEMENT_TYPE = Object.freeze({
  START: 3,
  END: 2,
  POI: 1,
  DEFAULT: -1 // On path, but not start/end/POI
  // 0 is reserved for off-path elements (not explicitly used)
});

const tmpLine3 = new THREE.Line3();
const tmpPlane = new THREE.Plane();
const tmpVec4_1 = new THREE.Vector4();
const tmpVec4_2 = new THREE.Vector4();
const tmpFrustum = new THREE.Frustum();


export class SystemVisualizer {

  // Multiple trace steps can be added to the draw queue
  // Eg. when displaying paths for multiple systems
  #traceSteps = [];

  // Tracks element type (start/end/POI) for all traced elements
  #tracedElementTypeByModel = new Map();

  isShowing = false;

  dispose() {
    this.hud = null;
  }

  init(facility) {
    this.facility = facility;
    this.viewer = facility.viewer;
    this.hud = this.facility.getHUD();
    this.animTick = 0;
  }

  /**
   * Add draw function to HUD's draw queue to visualize connections,
   * starting from a single element and traverse down to provided depth.
   * @returns Cleanup function
   */
  canvasConnectionsWithCleanup(systemElement, depth = 1) {
    if (!depth) {
      return () => {};
    }

    const traversed = new Set().add(systemElement);
    const traversalQueue = [systemElement];

    const nodePairs = [];
    let currentDepth = 0;

    const collectEdge = (element, connection) => {
      const start = element.getCenterPoint();
      const end = connection.getCenterPoint();

      if (start && end) {
        nodePairs.push(start, end);
      }

      if (currentDepth < depth && !traversed.has(connection)) {
        traversed.add(connection);
        traversalQueue.push(connection);
      }
    };

    // Run previously queued traversal
    while (traversalQueue.length) {
      currentDepth++;

      let depthSize = traversalQueue.length;
      while (depthSize > 0) {
        depthSize--;
        const element = traversalQueue.shift();
        element.enumConnections(null, collectEdge);
      }
    }

    function drawFunc(hud, mvpMatrix) {
      tmpFrustum.setFromProjectionMatrix(mvpMatrix);

      let planeNear = tmpFrustum.planes[5];
      let clipPlaneNear = tmpPlane.copy(planeNear).applyMatrix4(mvpMatrix);

      const edges = [];
      for (let i = 0; i < nodePairs.length; i += 2) {
        // Clone to avoid changing cached coordinates
        const start = nodePairs[i].clone();
        const end = nodePairs[i + 1].clone();

        let skip = false;

        for (const plane of tmpFrustum.planes) {
          const startToPlaneDist = plane.distanceToPoint(start);
          const endToPlaneDist = plane.distanceToPoint(end);
          if (startToPlaneDist < 0 && endToPlaneDist < 0) {
            // Skip edge if both points are culled by the same plane
            skip = true;
            break;
          }
        }

        if (skip) continue;

        clipAndProject(start, end, clipPlaneNear, mvpMatrix);

        start.x = (start.x * 0.5 + 0.5) * hud.width;
        start.y = (-start.y * 0.5 + 0.5) * hud.height;

        end.x = (end.x * 0.5 + 0.5) * hud.width;
        end.y = (-end.y * 0.5 + 0.5) * hud.height;

        edges.push([start, end]);
      }

      // Sort by depth, but it makes some lines blink in and out of existence
      edges.sort((e1, e2) => e2[0].z + e2[1].z - (e1[0].z + e1[1].z));

      for (let i = 0; i < edges.length; i++) {
        drawConnectionLine(hud.ctx, hud.dpr, edges[i][0], edges[i][1]);
      }
    }

    // Push to queue and trigger canvas redraw
    const drawOrder = -1;
    // TODO: Use same pattern as canvasMultiPaths instead of hacking draw step into init
    const drawStep = [drawOrder, { init: drawFunc }];
    this.hud.addDrawStep(drawStep);

    return () => {
      this.hud.removeDrawStep(drawStep);
    };
  }


  canvasMultiPaths(filteredTracePaths) {

    //console.log("START", startElements);
    //console.log("END", endElements);

    const { nodePairs, nodePairsSecondary } = filteredTracePaths.collectEdges();

    const visualizer = this;
    let edges,lastFrameTime = 0;

    const animStep = {
      init: (hud, mvpMatrix) => {
        edges = [];

        tmpFrustum.setFromProjectionMatrix(mvpMatrix);

        let planeNear = tmpFrustum.planes[5];
        let clipPlaneNear = tmpPlane.copy(planeNear).applyMatrix4(mvpMatrix);

        // Map to store distance from root
        const distFromRoot = new Map();

        const allNodeSets = [nodePairsSecondary, nodePairs];

        for (let j = 0; j < allNodeSets.length; j++) {

          const nodePairs = allNodeSets[j];

          for (let i = 0; i < nodePairs.length; i += 2) {

            if (nodePairs[i].distanceToSquared(nodePairs[i + 1]) <= EPSILON) {
              // Skip near zero length segments
              continue;
            }

            // Clone to avoid changing cached coordinates
            const start = nodePairs[i].clone();
            const end = nodePairs[i + 1].clone();

            let skip = false;

            for (const plane of tmpFrustum.planes) {
              const startToPlaneDist = plane.distanceToPoint(start);
              const endToPlaneDist = plane.distanceToPoint(end);
              if (startToPlaneDist < 0 && endToPlaneDist < 0) {
                // Skip edge if both points are culled by the same plane
                skip = true;
                break;
              }
            }

            if (skip) continue;

            clipAndProject(start, end, clipPlaneNear, mvpMatrix);

            start.x = (start.x * 0.5 + 0.5) * hud.width;
            start.y = (-start.y * 0.5 + 0.5) * hud.height;

            end.x = (end.x * 0.5 + 0.5) * hud.width;
            end.y = (-end.y * 0.5 + 0.5) * hud.height;

            const dx = end.x - start.x;
            const dy = end.y - start.y;
            const distSq = dx * dx + dy * dy;

            if (distSq <= EPSILON) {
              // Skip near zero length projected segments
              continue;
            }

            const startHash = hash2DCoord(start.x, start.y);
            const offset = distFromRoot.get(startHash) ?? 0;
            edges.push([start, end, offset, j]);

            // Store offset value for this edge's end point
            const endHash = hash2DCoord(end.x, end.y);
            if (!distFromRoot.has(endHash)) {
              const edgeLength = dist2D(start, end);
              const endOffset = offset + edgeLength;
              distFromRoot.set(endHash, endOffset);
            }
          }
        }

        // Sort by depth
        edges.sort((e1, e2) => e2[0].z + e2[1].z - (e1[0].z + e1[1].z));
      },
      update: (ts) => {
        const delta = ts - lastFrameTime;
        if (delta > ANIM_FRAME_INTERVAL) {
          visualizer.animTick += delta * CONNECTION_LINE_SPEED;
          visualizer.animTick %= ANIM_STEPS;
          lastFrameTime = ts;
          return true;
        }
      },
      drawStatic: (hud) => {
        hud.ctx.lineCap = "butt";

        // 1- Solid background line
        hud.ctx.strokeStyle = CANVAS_LINE_SHADOW_COLOR_TRACE;

        let lastImportance = -1;
        let pathEmpty = true;
        for (const [start, end, _, importance] of edges) {

          if (lastImportance !== importance) {
            !pathEmpty && hud.ctx.stroke();
            hud.ctx.beginPath();
            if (importance === 0) {
              hud.ctx.lineWidth = CANVAS_LINE_STROKE_WIDTH_TRACE_SECONDARY * hud.dpr;
            } else {
              hud.ctx.lineWidth = CANVAS_LINE_STROKE_WIDTH_TRACE * hud.dpr;
            }
          }

          hud.ctx.moveTo(start.x, start.y);
          hud.ctx.lineTo(end.x, end.y);
          lastImportance = importance;
          pathEmpty = false;
        }
        !pathEmpty && hud.ctx.stroke();

      },
      drawAnim: (hud) => {
        const animOffset = visualizer.animTick;
        hud.animCtx.lineCap = "butt";

        // 2- Dashed high contrast animated line on top
        hud.animCtx.setLineDash([CONNECTION_LINE_SEGMENT_LENGTH * hud.dpr, CONNECTION_LINE_SEGMENT_LENGTH * hud.dpr]);
        hud.animCtx.strokeStyle = CANVAS_LINE_COLOR_TRACE;

        //Must make multple strokes here because we vary the line dash offset per segment
        //hud.animCtx.lineDashOffset = -animOffset;
        //hud.animCtx.beginPath();
        for (const [start, end, offset, importance] of edges) {

          if (importance === 0) {
            hud.animCtx.lineWidth = CANVAS_LINE_STROKE_WIDTH_TRACE_SECONDARY * hud.dpr;
          } else {
            hud.animCtx.lineWidth = CANVAS_LINE_STROKE_WIDTH_TRACE * hud.dpr;
          }

          hud.animCtx.beginPath();
          hud.animCtx.lineDashOffset = offset - animOffset;
          hud.animCtx.moveTo(start.x, start.y);
          hud.animCtx.lineTo(end.x, end.y);
          hud.animCtx.stroke();
        }
        //hud.animCtx.stroke();
      }
    };

    // Push to queue and trigger canvas redraw
    const priority = -1;
    const drawStep = [priority, animStep];
    this.#traceSteps.push(drawStep);
    this.hud.addDrawStep(drawStep);
  }

  /**
   * Draw a set of system elements as overlays.
   *
   * All elements, not in foreground will be placed in an orange (background) overlay.
   * @param {Iterable} elements System elements to place in foreground overlay.
   * @param {Iterable} elementsBackground Elements to place in background overlay.
   * @returns Cleanup function
   */
  overlayHighlight(elements, elementsBackground) {
    for (const e of elements) {
      this.facility.facetsManager.facetsEffects.addElementHighlight(e.model, e.dbId, ELEMENT_CLONE_OVERLAY);
    }
    for (const e of elementsBackground) {
      this.facility.facetsManager.facetsEffects.addElementHighlight(e.model, e.dbId, BACK_ELEMENT_CLONE_OVERLAY);
    }
  }

  clearOverlayHighlights() {
    this.facility.facetsManager.facetsEffects.clearElementsOverlay(ELEMENT_CLONE_OVERLAY);
    this.facility.facetsManager.facetsEffects.clearElementsOverlay(BACK_ELEMENT_CLONE_OVERLAY);
  }

  highlightRoomsAlongTrace(elements) {

    const doneRooms = new Set();
    for (const e of elements) {
      const erooms = this.facility.getRoomsOfElement(e.model, e.dbId);

      for (const r of erooms) {
        const rid = r.model.id + ":" + r.dbId;
        if (doneRooms.has(rid)) {
          continue;
        }
        doneRooms.add(rid);
        this.facility.facetsManager.facetsEffects.addSpaceBoundaryViz(NAME_PREFIX_TRACE + r.dbId, r.model, r.dbId, COLOR_TRACE_HIGHLIGHT_ABGR);
      }
    }

  }

  resetRoomsAlongTrace() {
    this.facility.facetsManager.facetsEffects.removeSpacesFromOverlay((mesh) => {
      return mesh.name.startsWith(NAME_PREFIX_TRACE);
    });
  }

  showOneLabel(element) {
    // Add a new trace metadata entry for the start element
    const byDbId = new Map().set(element.dbId, TRACE_ELEMENT_TYPE.START);
    this.#tracedElementTypeByModel.set(element.model, byDbId);
    this.facility.viewer.dispatchEvent({
      type: dte.DT_TRACE_ELEMENTS_CHANGED_EVENT,
      tracedElementTypeByModel: this.#tracedElementTypeByModel
    });

    this.isShowing = true;
  }

  collectTracedElementTypes(filteredPaths) {
    const tracePaths = filteredPaths.tracePaths;

    const getHighestPriorityType = (element) => {
      if (element === tracePaths.startElement) {
        return TRACE_ELEMENT_TYPE.START;
      } else if (tracePaths.endElements.has(element)) {
        return TRACE_ELEMENT_TYPE.END;
      } else if (tracePaths.needsLabel(this.facility, element)) {
        return TRACE_ELEMENT_TYPE.POI;
      }
      return TRACE_ELEMENT_TYPE.DEFAULT;
    };

    for (const element of filteredPaths.elementsOnSelectedPaths) {
      const { model, dbId } = element;

      let byDbId = this.#tracedElementTypeByModel.get(model);
      if (!byDbId) {
        byDbId = new Map();
        this.#tracedElementTypeByModel.set(model, byDbId);
      }

      const currType = byDbId.get(dbId);
      const highestPriorityType = getHighestPriorityType(element);

      if (!currType || highestPriorityType > currType) {
        byDbId.set(dbId, highestPriorityType);
      }
    }
  }

  showTrace(filteredPaths, hasLine, hasOverlay, hasSpaces, showLabels) {

    this.isShowing = true;

    if (hasLine) {
      this.canvasMultiPaths(filteredPaths);
    }
    if (hasOverlay) {
      this.overlayHighlight(
        filteredPaths.elementsOnSelectedPaths,
        filteredPaths.elementsOffSelectedPaths
      );
    }
    if (hasSpaces) {
      this.highlightRoomsAlongTrace(filteredPaths.elementsOnSelectedPaths);
    }
    if (showLabels) {
      this.collectTracedElementTypes(filteredPaths);
      this.facility.viewer.dispatchEvent({
        type: dte.DT_TRACE_ELEMENTS_CHANGED_EVENT,
        tracedElementTypeByModel: this.#tracedElementTypeByModel
      });
    }
  }

  clearTrace() {

    if (!this.isShowing) {
      return;
    }

    this.clearOverlayHighlights();
    this.#traceSteps.forEach((step) => this.hud.removeDrawStep(step));
    this.#traceSteps.length = 0;
    this.resetRoomsAlongTrace();
    this.#tracedElementTypeByModel.clear();
    this.facility.viewer.dispatchEvent({
      type: dte.DT_TRACE_ELEMENTS_CHANGED_EVENT,
      tracedElementTypeByModel: this.#tracedElementTypeByModel
    });

    this.isShowing = false;
  }
}


function drawConnectionLine(ctx, dpr, start, end) {

  // Three strokes per edge
  ctx.beginPath();

  ctx.moveTo(start.x, start.y);
  ctx.lineTo(end.x, end.y);

  ctx.globalCompositeOperation = "source-over";

  // 1- Contrast line (open cap), drawn on top of existing strokes
  ctx.lineCap = "butt";
  ctx.lineWidth = (CANVAS_LINE_STROKE_WIDTH + 2) * dpr;
  ctx.strokeStyle = CANVAS_LINE_SHADOW_COLOR;
  ctx.stroke();

  // 2- Connection line, drawn on top of existing strokes
  ctx.lineCap = "round";
  ctx.lineWidth = CANVAS_LINE_STROKE_WIDTH * dpr;
  ctx.strokeStyle = CANVAS_LINE_COLOR;
  ctx.stroke();

  ctx.globalCompositeOperation = "destination-over";

  // 3- Contrast line (round cap), drawn underneath existing strokes
  ctx.lineCap = "round";
  ctx.lineWidth = (CANVAS_LINE_STROKE_WIDTH + 2) * dpr;
  ctx.strokeStyle = CANVAS_LINE_SHADOW_COLOR;
  ctx.stroke();
}

function clipAndProject(start, end, clipPlaneNear, m) {
  // Project start/end into clip space to check against near plane
  const clipSpaceStart = tmpVec4_1.copy(start).applyMatrix4(m);
  const clipSpaceEnd = tmpVec4_2.copy(end).applyMatrix4(m);

  if (clipPlaneNear.distanceToPoint(clipSpaceStart) < 0) {
    tmpLine3.set(clipSpaceEnd, clipSpaceStart);
    // Clip start
    clipPlaneNear.intersectLine(tmpLine3, start);
  } else {
    // No clipping needed, do perspective divide
    start.copy(clipSpaceStart);
    start.multiplyScalar(1.0 / clipSpaceStart.w);
  }

  if (clipPlaneNear.distanceToPoint(clipSpaceEnd) < 0) {
    tmpLine3.set(clipSpaceStart, clipSpaceEnd);
    // Clip end
    clipPlaneNear.intersectLine(tmpLine3, end);
  } else {
    // No clipping needed, do perspective divide
    end.copy(clipSpaceEnd);
    end.multiplyScalar(1.0 / clipSpaceEnd.w);
  }
}

function dist2D(p1, p2) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const distSq = dx * dx + dy * dy;
  return Math.sqrt(distSq);
}

const HASH_PRIME = 7927;
function hash2DCoord(x, y) {
  return x * 100 * HASH_PRIME ^ y * 100;
}